import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export enum MeanType {
  geo = 1,
  ari = 2,
}

/*
    Json output from server
{
"key": "RRB",
"r_squared": 0.9990909617,
"error_mean": 0.0099653865,
"model_repr": "X(d) = 1 - exp(-(d/D63)^n)",
"parameters": [
  {
    "repr": "D63",
    "value": 87.5171224616,
    "stddev": 0.6983426178
  },
  {
    "repr": "n",
    "value": 1.0113310817,
    "stddev": 0.0096541619
  }
}
      */

export interface Models {
  key: string;
  data: string;
}

export interface ModelSingleParameter {
  repr: string;
  value: number;
  stddev: number;
}
export interface ModelParameters {
  key: string;
  model_repr: string;
  parameters: Array<ModelSingleParameter>;
  r_squared: number;
  error_mean: number;
}

interface AppStateType {
  xpsfiles: Array<string>;
  filesList: Array<File>;
  basenames: Array<string>;
  dirnames: Array<string>;
  outName: string;
  singleSVGfile: string;
  outDirName: string;
  isLogScale: boolean;
  meanType: MeanType;
  currentModel: string;
  multiLabel: boolean;
  zerosLeft: number;
  zerosRight: number;
  isComputing: boolean;
  isSpinnerHidden: boolean;
  isSingleFile: boolean;
  isServerOn: boolean;
  isXPSEmpty: boolean;
  showZip: boolean;
  blobHref: string;
  downloadName: string;
  singleCurveSVG: string;
  models: Array<Models>;
  version: string;
  current_models_parameters: Array<ModelParameters>;
}

const initialState = {
  xpsfiles: [],
  basenames: [],
  dirnames: [],
  filesList: [],
  outName: "",
  outDirName: "",
  singleSVGfile: "",
  isLogScale: true,
  multiLabel: true,
  isServerOn: false,
  meanType: MeanType.geo,
  currentModel: "RRB",
  zerosLeft: 1,
  zerosRight: 1,
  isSpinnerHidden: true,
  isComputing: false,
  isSingleFile: true,
  isXPSEmpty: true,
  showZip: false,
  blobHref: "",
  downloadName: "",
  singleCurveSVG: "",
  version: "not versioned",
  models: [],
  current_models_parameters: [],
} as AppStateType;

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setXPSFiles(state: AppStateType, action: PayloadAction<Array<string>>) {
      state.xpsfiles = action.payload;
    },
    setBasenames(state: AppStateType, action: PayloadAction<Array<string>>) {
      state.basenames = action.payload;
    },
    setDirnames(state: AppStateType, action: PayloadAction<Array<string>>) {
      state.dirnames = action.payload;
    },
    setOutName(state: AppStateType, action: PayloadAction<string>) {
      state.outName = action.payload;
    },
    setSingleSVGfile(state: AppStateType, action: PayloadAction<string>) {
      state.singleSVGfile = action.payload;
    },
    setOutDir(state: AppStateType, action: PayloadAction<string>) {
      state.outDirName = action.payload;
    },
    toggleIsLogScale(state: AppStateType) {
      state.isLogScale = !state.isLogScale;
    },
    toggleMultiLabel(state: AppStateType) {
      state.multiLabel = !state.multiLabel;
    },
    setMeanType(state: AppStateType, action: PayloadAction<MeanType>) {
      state.meanType = action.payload;
    },
    setCurrentModel(state: AppStateType, action: PayloadAction<string>) {
      state.currentModel = action.payload;
    },
    setBlobHref(state: AppStateType, action: PayloadAction<string>) {
      state.blobHref = action.payload;
    },
    setDownloadName(state: AppStateType, action: PayloadAction<string>) {
      state.downloadName = action.payload;
    },
    setSingleCurveSVG(state: AppStateType, action: PayloadAction<string>) {
      state.singleCurveSVG = action.payload;
    },
    setVersion(state: AppStateType, action: PayloadAction<string>) {
      state.version = action.payload;
    },
    setFilesList(state: AppStateType, action: PayloadAction<Array<File>>) {
      state.filesList = action.payload;
    },
    setModels(state: AppStateType, action: PayloadAction<Array<Models>>) {
      state.models = action.payload;
    },
    setZerosLeft(state: AppStateType, action: PayloadAction<number>) {
      state.zerosLeft = action.payload;
    },
    setZerosRight(state: AppStateType, action: PayloadAction<number>) {
      state.zerosRight = action.payload;
    },
    setIsSpinnerHidden(state: AppStateType, action: PayloadAction<boolean>) {
      state.isSpinnerHidden = action.payload;
    },
    setIsXPSEmpty(state: AppStateType, action: PayloadAction<boolean>) {
      state.isXPSEmpty = action.payload;
    },
    setIsComputing(state: AppStateType, action: PayloadAction<boolean>) {
      state.isComputing = action.payload;
    },
    setIsServerOn(state: AppStateType, action: PayloadAction<boolean>) {
      state.isServerOn = action.payload;
    },
    setShowZip(state: AppStateType, action: PayloadAction<boolean>) {
      state.showZip = action.payload;
    },
    setIsSingleFile(state: AppStateType, action: PayloadAction<boolean>) {
      state.isSingleFile = action.payload;
    },

    setCurrentModelParameters(
      state: AppStateType,
      action: PayloadAction<Array<ModelParameters>>
    ) {
      state.current_models_parameters = action.payload;
    },
  },
});

export const {
  setXPSFiles,
  setBasenames,
  setDirnames,
  setCurrentModel,
  setOutName,
  setOutDir,
  toggleIsLogScale,
  toggleMultiLabel,
  setZerosLeft,
  setZerosRight,
  setMeanType,
  setIsSpinnerHidden,
  setIsComputing,
  setIsServerOn,
  setIsSingleFile,
  setSingleSVGfile,
  setIsXPSEmpty,
  setFilesList,
  setShowZip,
  setDownloadName,
  setBlobHref,
  setSingleCurveSVG,
  setModels,
  setVersion,
  setCurrentModelParameters,
} = appSlice.actions;
export default appSlice.reducer;
